import {
  FormControl,
  FormErrorMessage,
  Input,
  InputGroup,
  InputLeftElement,
  Flex,
  InputRightElement,
  Tooltip,
  CloseButton,
  FormErrorIcon,
} from '@chakra-ui/react';
import { t } from '@transifex/native';
import { T } from '@transifex/react';
import { useMemo, useCallback, ChangeEvent } from 'react';
import { getTableOptions } from './membersTableHelpers';
import { useMemberGuards } from 'features/members/utils/lib';
import { allowedSearchCharactersRegex } from 'features/common/validations/matchers';
import { EmptyTablePlaceholder } from './components/EmptyTablePlaceHolder';
import { mdiMagnify } from '@mdi/js';
import styled from '@emotion/styled';
import {
  DataTable,
  DataTableListOptions,
  DataTableSortingRule,
} from 'features/common/components/DataTable';
import { PortalIcon } from 'features/common/components/PortalIcon';
import { adminPath, membersPath } from 'features/admin/components/admin-paths';
import { useMembersData } from '../../hooks/useMembersData/useMembersData';
import { withErrorBoundary } from 'features/common/utils/errorBoundaries';
import { MembersErrorPage } from 'features/main/components/CustomErrorPages/MembersErrorPage';
import { RouteComponentProps } from 'react-router-dom';

const TableDiv = styled.div`
  table {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
  }
`;

const sortBy: DataTableSortingRule[] | undefined = [];

export type IMembersTableProps = RouteComponentProps<{ page: string }>;

export const MembersTable = (props: IMembersTableProps) => {
  const {
    memberToShow,
    totalCount,
    membersLoading,
    searchFilter,
    setSearchFilter,
    searchStringIsValid,
    setSearchStringIsValid,
    page,
    pageSize,
    pagination,
    setResetPagination,
    history,
  } = useMembersData(props);

  const { allowMemberAccess } = useMemberGuards();

  const options = useMemo(
    () =>
      getTableOptions({
        page,
        allowMemberAccess,
      }),
    [allowMemberAccess, page],
  );

  const onChange = useCallback(
    (options: DataTableListOptions) => {
      setResetPagination(false);
      history.push({
        pathname: `${adminPath}${membersPath}/page/${options.page}`,
        search: history.location.search,
      });
    },
    [history, setResetPagination],
  );

  const onSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const searchInputValue = event.target.value;

    if (allowedSearchCharactersRegex.test(searchInputValue)) {
      setSearchFilter(searchInputValue);
      setSearchStringIsValid(true);
    } else {
      setSearchStringIsValid(false);
    }
  };

  const onClearHandler = () => {
    setSearchFilter('');
    setSearchStringIsValid(true);
  };

  return (
    <>
      <Flex justifyContent='space-between' marginBottom={12} maxW='xs'>
        <FormControl isInvalid={!searchStringIsValid}>
          <InputGroup mb='4' maxWidth='sm'>
            <InputLeftElement
              pointerEvents='none'
              // TODO change this after we open Input, InputElement props
              children={<PortalIcon path={mdiMagnify} />}
            />
            <Input
              placeholder='Search'
              data-testid='member_search_input'
              data-behavior-analytics-feature='members search'
              onChange={onSearch}
              value={searchFilter}
            />
            {searchFilter.length > 0 && (
              <InputRightElement>
                <Tooltip label='Clear search'>
                  <CloseButton
                    onClick={onClearHandler}
                    data-testid='member_search_clear_button'
                  />
                </Tooltip>
              </InputRightElement>
            )}
          </InputGroup>
          <FormErrorMessage>
            <FormErrorIcon />
            <T _str='Currently, the search is only available in alphanumeric characters, at signs (@), dots, dashes, and underscores.' />
          </FormErrorMessage>
        </FormControl>
      </Flex>

      {memberToShow.length === 0 && !membersLoading ? (
        <EmptyTablePlaceholder
          description={t('Change your search parameters and try again.')}
          title={t('No team members found')}
        />
      ) : (
        <TableDiv>
          <DataTable
            columns={options.columns}
            //@ts-ignore
            rowActions={options.rowActions}
            pagination={pagination as 'hidden' | 'complex'}
            totalItems={totalCount}
            data={memberToShow}
            onChange={onChange}
            page={page}
            pageSize={pageSize}
            isLoading={membersLoading}
            sortBy={sortBy}
            theaderProps={{ 'data-behavior-analytics-id': 'members sorting' }}
            paginationProps={{
              'data-behavior-analytics-feature': ' members pagination',
            }}
            data-behavior-analytics-feature='members table'
          />
        </TableDiv>
      )}
    </>
  );
};

export const MembersTableWithErrorHandling = withErrorBoundary(MembersTable, MembersErrorPage);
